import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader, AlertCircle, Check, X, Pause, Play, RefreshCw,
  Send, Trash2, Mail, Download, Wallet, FileSpreadsheet, ExternalLink
} from 'lucide-react';
import { apiCallJson as apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

/**
 * Admin Affiliates panel
 * ----------------------
 * Three tabs:
 *   1. Affiliates  — list, approve/reject/pause, see balance + ref code
 *   2. Invites     — issue email invites, resend, revoke
 *   3. Payouts     — preview eligible affiliates, generate batch + CSV, mark paid
 */

const STATUS_PILL = {
  pending:  { label: 'Pending',  color: '#f59e0b' },
  approved: { label: 'Approved', color: '#10b981' },
  rejected: { label: 'Rejected', color: '#ef4444' },
  paused:   { label: 'Paused',   color: '#6b7280' }
};

const PAYOUT_STATUS = {
  pending:  { label: 'Pending',  color: '#f59e0b' },
  exported: { label: 'Exported', color: '#3b82f6' },
  paid:     { label: 'Paid',     color: '#10b981' },
  failed:   { label: 'Failed',   color: '#ef4444' }
};

function formatEuros(cents) {
  if (cents === null || cents === undefined) return '—';
  const negative = parseInt(cents) < 0;
  const value = (Math.abs(parseInt(cents)) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
  return `${negative ? '−' : ''}€${value}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Pill({ cfg }) {
  if (!cfg) return null;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px',
      borderRadius: 6, fontSize: 11, fontWeight: 600,
      background: `${cfg.color}1f`, color: cfg.color,
      textTransform: 'uppercase', letterSpacing: '0.04em'
    }}>{cfg.label}</span>
  );
}

function PrimaryButton({ children, onClick, disabled, variant = 'primary', icon: Icon, theme, size = 'md' }) {
  const colors = {
    primary: { bg: '#6366f1', fg: 'white' },
    success: { bg: '#10b981', fg: 'white' },
    danger:  { bg: '#ef4444', fg: 'white' },
    muted:   { bg: theme.colors.bg.tertiary, fg: theme.colors.text.primary }
  }[variant];
  const padding = size === 'sm' ? '5px 10px' : '8px 14px';
  const fontSize = size === 'sm' ? 12 : 13;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: colors.bg, color: colors.fg,
        border: 'none', borderRadius: 8,
        padding, fontSize, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 6
      }}
    >
      {Icon && <Icon size={size === 'sm' ? 12 : 14} />}
      {children}
    </button>
  );
}

// --------------------------------------------------------------------------
// Tab 1: Affiliates list
// --------------------------------------------------------------------------

function AffiliatesTab({ theme, toast }) {
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState([]);
  const [busy, setBusy] = useState(null); // { id, action }
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = statusFilter === 'all'
        ? '/api/admin/affiliates'
        : `/api/admin/affiliates?status=${statusFilter}`;
      const res = await apiCall(path);
      setAffiliates(res?.affiliates || []);
    } catch (e) {
      toast?.error(e.message || 'Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy({ id, action });
    try {
      const res = await apiCall(`/api/admin/affiliates/${id}/${action}`, { method: 'POST' });
      if (!res?.success) throw new Error(res?.error || 'Failed');
      toast?.success(`Affiliate ${action}d`);
      await load();
    } catch (e) {
      toast?.error(e.message || `Failed to ${action} affiliate`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            background: theme.colors.bg.secondary,
            color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.border.primary}`,
            borderRadius: 8, padding: '6px 10px', fontSize: 13
          }}
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="paused">Paused</option>
          <option value="rejected">Rejected</option>
        </select>
        <PrimaryButton onClick={load} icon={RefreshCw} variant="muted" theme={theme} size="sm">
          Refresh
        </PrimaryButton>
        <div style={{ flex: 1 }} />
        <div style={{ color: theme.colors.text.tertiary, fontSize: 12 }}>
          {affiliates.length} {affiliates.length === 1 ? 'affiliate' : 'affiliates'}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: theme.colors.text.secondary }}>
          <Loader size={18} /> Loading…
        </div>
      ) : affiliates.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: theme.colors.text.tertiary }}>
          No affiliates match this filter.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Name / email</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Ref code</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Lifetime</th>
                <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Pending</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Joined</th>
                <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map(a => (
                <tr key={a.id} style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ color: theme.colors.text.primary, fontWeight: 500 }}>
                      {a.display_name || a.user_full_name || '—'}
                    </div>
                    <div style={{ color: theme.colors.text.tertiary, fontSize: 12 }}>
                      {a.user_email || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary, fontFamily: 'monospace', fontSize: 12 }}>
                    {a.ref_code}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Pill cfg={STATUS_PILL[a.status]} />
                    {a.rejected_reason && (
                      <div style={{ fontSize: 11, color: theme.colors.text.tertiary, marginTop: 4 }}>
                        {a.rejected_reason}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: theme.colors.text.primary }}>
                    {formatEuros(a.lifetime_commission_cents)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>
                    {formatEuros(a.pending_payout_cents)}
                  </td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>
                    {formatDate(a.created_at)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      {a.status === 'pending' && (
                        <>
                          <PrimaryButton onClick={() => act(a.id, 'approve')} variant="success" icon={Check} size="sm" theme={theme}
                            disabled={busy?.id === a.id}>Approve</PrimaryButton>
                          <PrimaryButton onClick={() => {
                            const reason = window.prompt('Rejection reason (optional)?');
                            if (reason !== null) act(a.id, 'reject');
                          }} variant="danger" icon={X} size="sm" theme={theme}
                            disabled={busy?.id === a.id}>Reject</PrimaryButton>
                        </>
                      )}
                      {a.status === 'approved' && (
                        <PrimaryButton onClick={() => act(a.id, 'pause')} variant="muted" icon={Pause} size="sm" theme={theme}
                          disabled={busy?.id === a.id}>Pause</PrimaryButton>
                      )}
                      {a.status === 'paused' && (
                        <PrimaryButton onClick={() => act(a.id, 'approve')} variant="success" icon={Play} size="sm" theme={theme}
                          disabled={busy?.id === a.id}>Resume</PrimaryButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Tab 2: Invites
// --------------------------------------------------------------------------

function InvitesTab({ theme, toast }) {
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', displayName: '', commissionPct: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/affiliates/invites');
      setInvites(res?.invites || []);
    } catch (e) {
      toast?.error(e.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.email.trim()) {
      toast?.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        email: form.email.trim(),
        displayName: form.displayName.trim() || null,
        commissionPct: form.commissionPct ? parseFloat(form.commissionPct) : null,
        message: form.message.trim() || null
      };
      const res = await apiCall('/api/admin/affiliates/invites', { method: 'POST', body });
      if (!res?.success) throw new Error(res?.error || 'Failed');
      toast?.success(res.emailDelivered
        ? `Invite sent to ${body.email}`
        : `Invite created but email failed to send (check logs)`);
      setShowForm(false);
      setForm({ email: '', displayName: '', commissionPct: '', message: '' });
      await load();
    } catch (e) {
      toast?.error(e.message || 'Failed to create invite');
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (id) => {
    try {
      const res = await apiCall(`/api/admin/affiliates/invites/${id}/resend`, { method: 'POST' });
      if (!res?.success) throw new Error(res?.error);
      toast?.success(res.emailDelivered ? 'Invite resent' : 'Token refreshed (email send failed)');
      await load();
    } catch (e) {
      toast?.error(e.message || 'Failed to resend');
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this invite? The recipient will no longer be able to claim it.')) return;
    try {
      const res = await apiCall(`/api/admin/affiliates/invites/${id}`, { method: 'DELETE' });
      if (!res?.success) throw new Error(res?.error);
      toast?.success('Invite revoked');
      await load();
    } catch (e) {
      toast?.error(e.message || 'Failed to revoke');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <PrimaryButton onClick={() => setShowForm(s => !s)} icon={Mail} theme={theme}>
          {showForm ? 'Cancel' : 'New invite'}
        </PrimaryButton>
        <PrimaryButton onClick={load} icon={RefreshCw} variant="muted" theme={theme} size="sm">
          Refresh
        </PrimaryButton>
        <div style={{ flex: 1 }} />
        <div style={{ color: theme.colors.text.tertiary, fontSize: 12 }}>
          {invites.length} pending
        </div>
      </div>

      {showForm && (
        <div style={{
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 12, padding: 18, marginBottom: 16
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: theme.colors.text.secondary }}>
                Email *
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="affiliate@example.com"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: theme.colors.bg.primary, color: theme.colors.text.primary,
                  border: `1px solid ${theme.colors.border.primary}`, borderRadius: 8,
                  padding: '8px 10px', fontSize: 13
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: theme.colors.text.secondary }}>
                Display name (optional)
              </label>
              <input
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder="Anna van Dijk"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: theme.colors.bg.primary, color: theme.colors.text.primary,
                  border: `1px solid ${theme.colors.border.primary}`, borderRadius: 8,
                  padding: '8px 10px', fontSize: 13
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: theme.colors.text.secondary }}>
                Custom commission % (optional)
              </label>
              <input
                type="number" step="0.5" min="1" max="100"
                value={form.commissionPct}
                onChange={(e) => setForm({ ...form, commissionPct: e.target.value })}
                placeholder="22"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: theme.colors.bg.primary, color: theme.colors.text.primary,
                  border: `1px solid ${theme.colors.border.primary}`, borderRadius: 8,
                  padding: '8px 10px', fontSize: 13
                }}
              />
            </div>
          </div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: theme.colors.text.secondary }}>
            Personal message (optional)
          </label>
          <textarea
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Hi Anna, I'd love for you to join our affiliate program..."
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: theme.colors.bg.primary, color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border.primary}`, borderRadius: 8,
              padding: '8px 10px', fontSize: 13,
              fontFamily: 'inherit', resize: 'vertical'
            }}
          />
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <PrimaryButton onClick={submit} disabled={submitting} icon={Send} theme={theme}>
              {submitting ? 'Sending…' : 'Send invite'}
            </PrimaryButton>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: theme.colors.text.secondary }}>
          <Loader size={18} /> Loading…
        </div>
      ) : invites.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: theme.colors.text.tertiary }}>
          No pending invites.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Display name</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Commission</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Sent</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Expires</th>
                <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map(i => (
                <tr key={i.id} style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.primary, fontWeight: 500 }}>{i.email}</td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{i.display_name || '—'}</td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>
                    {i.commission_pct ? `${Number(i.commission_pct)}% (custom)` : 'default'}
                  </td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>
                    {formatDate(i.last_sent_at)} {i.send_count > 1 && <span style={{ color: theme.colors.text.tertiary }}>· ×{i.send_count}</span>}
                  </td>
                  <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{formatDate(i.expires_at)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <PrimaryButton onClick={() => resend(i.id)} variant="muted" icon={Send} size="sm" theme={theme}>Resend</PrimaryButton>
                      <PrimaryButton onClick={() => revoke(i.id)} variant="danger" icon={Trash2} size="sm" theme={theme}>Revoke</PrimaryButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Tab 3: Payouts
// --------------------------------------------------------------------------

function PayoutsTab({ theme, toast }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState({ affiliates: [], totalPayableCents: 0, minPayoutCents: 10000 });
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null); // { generated, payouts, csv }
  const [pendingPayouts, setPendingPayouts] = useState([]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/affiliates/payouts/preview');
      setPreview({
        affiliates: res?.affiliates || [],
        totalPayableCents: res?.totalPayableCents || 0,
        minPayoutCents: res?.minPayoutCents || 10000
      });
    } catch (e) {
      toast?.error(e.message || 'Failed to load payout preview');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const generate = async () => {
    if (!window.confirm(`Generate payout batch for ${preview.affiliates.length} affiliates totalling ${formatEuros(preview.totalPayableCents)}?\n\nThis will:\n  - Move all unpaid commissions into a payout batch\n  - Reset their pending balance to zero\n  - Give you a CSV to import into your bank\n\nYou'll still need to mark each payout as "paid" after the actual SEPA transfer.`)) return;
    setGenerating(true);
    try {
      const res = await apiCall('/api/admin/affiliates/payouts/generate', { method: 'POST' });
      if (!res?.success) throw new Error(res?.error || 'Failed');
      setGenerated(res);
      setPendingPayouts(res.payouts);
      toast?.success(`Generated ${res.generated} payout(s)`);
      await loadPreview();
    } catch (e) {
      toast?.error(e.message || 'Failed to generate payouts');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCsv = () => {
    if (!generated?.csv) return;
    const blob = new Blob([generated.csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bidoryx-affiliate-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const markPaid = async (payoutId) => {
    const reference = window.prompt('Bank reference / transaction id?');
    if (reference === null) return;
    try {
      const res = await apiCall(`/api/admin/affiliates/payouts/${payoutId}/mark-paid`, {
        method: 'POST',
        body: { reference: reference || null }
      });
      if (!res?.success) throw new Error(res?.error);
      toast?.success('Payout marked as paid');
      setPendingPayouts(prev => prev.map(p => p.id === payoutId ? { ...p, status: 'paid', reference } : p));
    } catch (e) {
      toast?.error(e.message || 'Failed to mark paid');
    }
  };

  return (
    <div>
      {/* Eligible affiliates section */}
      <div style={{
        background: theme.colors.bg.secondary,
        border: `1px solid ${theme.colors.border.primary}`,
        borderRadius: 12, padding: 18, marginBottom: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <Wallet size={18} color="#10b981" />
          <div style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 700 }}>
            Eligible for payout
          </div>
          <div style={{ flex: 1 }} />
          <PrimaryButton onClick={loadPreview} icon={RefreshCw} variant="muted" size="sm" theme={theme}>Refresh</PrimaryButton>
          <PrimaryButton onClick={generate} disabled={generating || preview.affiliates.length === 0} icon={FileSpreadsheet} variant="success" theme={theme}>
            {generating ? 'Generating…' : `Generate batch (${formatEuros(preview.totalPayableCents)})`}
          </PrimaryButton>
        </div>

        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: theme.colors.text.secondary }}>
            <Loader size={16} /> Loading…
          </div>
        ) : preview.affiliates.length === 0 ? (
          <div style={{ padding: 18, textAlign: 'center', color: theme.colors.text.tertiary, fontSize: 13 }}>
            No affiliates have reached the {formatEuros(preview.minPayoutCents)} payout threshold.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Affiliate</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>IBAN</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Holder</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Commissions</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Payable</th>
                </tr>
              </thead>
              <tbody>
                {preview.affiliates.map(a => (
                  <tr key={a.affiliate_id} style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ color: theme.colors.text.primary, fontWeight: 500 }}>{a.display_name}</div>
                      <div style={{ color: theme.colors.text.tertiary, fontSize: 12, fontFamily: 'monospace' }}>{a.ref_code}</div>
                    </td>
                    <td style={{ padding: '10px 12px', color: theme.colors.text.secondary, fontFamily: 'monospace', fontSize: 12 }}>
                      {a.payout_iban || <span style={{ color: '#ef4444' }}>missing!</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{a.payout_holder || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: theme.colors.text.secondary }}>{a.commission_count}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#10b981', fontWeight: 700 }}>
                      {formatEuros(a.payable_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generated batch — appears after click */}
      {generated && (
        <div style={{
          background: theme.colors.bg.secondary,
          border: `1px solid #10b981`,
          borderRadius: 12, padding: 18, marginBottom: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <Check size={18} color="#10b981" />
            <div style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 700 }}>
              Batch generated · {generated.generated} payout(s)
            </div>
            <div style={{ flex: 1 }} />
            <PrimaryButton onClick={downloadCsv} icon={Download} variant="success" theme={theme}>
              Download CSV
            </PrimaryButton>
          </div>

          <div style={{ background: theme.colors.bg.primary, borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
            <strong style={{ color: theme.colors.text.primary }}>Next steps:</strong>
            <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
              <li>Download the CSV and upload it as a SEPA bulk transfer in your bank.</li>
              <li>After the transfer goes through, click "Mark paid" next to each payout below.</li>
              <li>The affiliates get a notification automatically once you mark theirs as paid.</li>
            </ol>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Affiliate</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayouts.map(p => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ color: theme.colors.text.primary, fontWeight: 500 }}>{p.display_name}</div>
                      <div style={{ color: theme.colors.text.tertiary, fontSize: 12, fontFamily: 'monospace' }}>{p.payout_iban}</div>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: theme.colors.text.primary, fontWeight: 700 }}>
                      {formatEuros(p.amount_cents)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Pill cfg={PAYOUT_STATUS[p.status]} />
                      {p.reference && <div style={{ fontSize: 11, color: theme.colors.text.tertiary, marginTop: 2 }}>{p.reference}</div>}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      {p.status !== 'paid' && (
                        <PrimaryButton onClick={() => markPaid(p.id)} variant="success" icon={Check} size="sm" theme={theme}>
                          Mark paid
                        </PrimaryButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Container
// --------------------------------------------------------------------------

export default function AdminAffiliates() {
  const { theme } = useTheme();
  const toast = useToast();
  const [tab, setTab] = useState('affiliates');

  const tabs = [
    { id: 'affiliates', label: 'Affiliates', icon: BadgeIcon },
    { id: 'invites',    label: 'Invites',    icon: Mail },
    { id: 'payouts',    label: 'Payouts',    icon: Wallet }
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <h1 style={{ color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, margin: 0 }}>
          Affiliate Program
        </h1>
        <div style={{
          padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
          background: '#ef44441f', color: '#ef4444',
          textTransform: 'uppercase', letterSpacing: '0.04em'
        }}>Admin</div>
      </div>

      <div style={{
        display: 'flex', gap: 4,
        marginBottom: 16,
        borderBottom: `1px solid ${theme.colors.border.primary}`
      }}>
        {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                background: 'none', border: 'none',
                padding: '10px 16px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
                color: active ? '#6366f1' : theme.colors.text.secondary,
                borderBottom: active ? '2px solid #6366f1' : '2px solid transparent',
                marginBottom: -1,
                display: 'inline-flex', alignItems: 'center', gap: 6
              }}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'affiliates' && <AffiliatesTab theme={theme} toast={toast} />}
      {tab === 'invites' && <InvitesTab theme={theme} toast={toast} />}
      {tab === 'payouts' && <PayoutsTab theme={theme} toast={toast} />}
    </div>
  );
}

// Lucide doesn't export a "Badge" icon directly — use the percent variant we already pull in elsewhere.
function BadgeIcon(props) {
  return <ExternalLink {...props} />;
}
